import { Box, BoxProps, Button, Flex, Icon, Link, Text } from '@chakra-ui/react';
import { useResource, useResources } from 'datx-jsonapi-react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import ContactUs from '@/assets/icons/ic-nav-help-desk.svg';
import TicketIcon from '@/assets/icons/ic-ticket.svg';

import { EventTicketDescriptionMarkdown } from '@/components/ui/shared/markdowns/EventTicketDescriptionMarkdown/EventTicketDescriptionMarkdown';
import { IToastType } from '@/enums/ToastType';
import useSession from '@/hooks/useSession';
import { useStores } from '@/hooks/useStores';
import { useToast } from '@/hooks/useToast';
import { queryEventTicketForUser } from '@/queries/event-tickets';
import { queryThinEvent } from '@/queries/thin-events';
import { EventTicket } from '@/resources/EventTicket';

interface ISelfRegisteredProps extends BoxProps {
  eventId: string;
}

export const SelfRegistered: FC<ISelfRegisteredProps> = ({ eventId, ...props }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { store } = useStores();
  const toast = useToast();
  const { user } = useSession();

  const { data: event } = useResource(queryThinEvent(eventId));
  const { mutate: ticketsMutate } = useResources(queryEventTicketForUser(eventId, user?.id));

  const onSelfRegisterClick = async () => {
    try {
      const ticket = store.add({ event }, EventTicket);

      await ticket.save();
      await ticketsMutate();
      toast({ status: IToastType.SUCCESS, title: t('eventTicketRegister.ticketCreated') });
    } catch (e) {
      toast({ status: IToastType.ERROR, title: t('eventTicketRegister.ticketError') });
    }
  };

  return (
    <Box shadow="card" bgColor="white" borderRadius={2} p={4} {...props}>
      <Box borderRadius={2} bgColor="gray.50" p={4} mb={{ base: 4, lg: 8 }}>
        <Flex justifyContent="flex-start" alignItems="center" mb={2}>
          <Icon w={6} h={6} as={TicketIcon} mr={2} color="black" />
          <Text fontWeight="bold" fontSize="1.125rem" lineHeight="short">
            {t('eventTicketRegister.selfRegistrationTitle')}
          </Text>
        </Flex>
        {event.missingTicketDescription ? (
          <EventTicketDescriptionMarkdown source={event.missingTicketDescription} />
        ) : (
          <Text>{t('eventTicketRegister.selfRegistrationMessage')}</Text>
        )}
      </Box>
      <Flex mb={6} flexDirection={{ base: 'column', lg: 'row' }} alignItems="center">
        <Box w={{ base: 'full', md: '50%' }} mr={{ base: 0, lg: 4 }} mb={{ base: 4, lg: 0 }}>
          {!user ? (
            <NextLink href="/create-account" passHref legacyBehavior>
              <Button as="a" target="_blank" size="sm" width="full">
                {t('eventTicketRegister.createAccount')}
              </Button>
            </NextLink>
          ) : (
            <Fragment>
              <Button onClick={onSelfRegisterClick} size="sm" width="full">
                {t('eventTicketRegister.selfRegisterAction')}
              </Button>
            </Fragment>
          )}
        </Box>
        {!user && (
          <Flex justifyContent="flex-start" w={['full', null, 'inherit']}>
            <Text as="span" mr={4}>
              {t('eventTicketRegister.alreadyRegistered')}
            </Text>
            <NextLink href={`/log-in?redirectUrl=${router.asPath}`} passHref legacyBehavior>
              <Link fontWeight="bold">{t('eventTicketRegister.logIn')}</Link>
            </NextLink>
          </Flex>
        )}
      </Flex>
      <Box>
        <Text as="span" mr={4}>
          {t('eventTicketRegister.needHelp')}
        </Text>
        <NextLink
          href={{
            pathname: '/contact-us/support',
            query: { eventId: event.id },
          }}
          passHref
          legacyBehavior
        >
          <Link fontWeight="bold">
            {t('eventTicketRegister.contactUs')} <Icon as={ContactUs} ml={2} />
          </Link>
        </NextLink>
      </Box>
    </Box>
  );
};
